import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui';
import CallInGhl from '../components/CallInGhl';
import { api, type LeadReason, type LeadRole, type LeadRow, type LeadStatus, type LeadTouch, type LeadsBoard } from '../lib/api';

/**
 * LEADS-PAGE (2026-10-26) - the living leads list: every GoHighLevel contact tagged `lead`, one tab per property tag,
 * "ball in our court" first. ⛔ READ-ONLY - the only action is Call in GHL, which opens the contact in GoHighLevel.
 * ⭐ This is where the Apex Leads agent's findings land (LEADS1): a row's reasons use its trigger words.
 * The server answers at once and fills each lead's messages and notes in the background; the page asks again every
 * few seconds until every row is read.
 */

export const ALL = 'all';
const POLL_MS = 4000;
const NOT_READ = "Couldn't read the leads from GoHighLevel - try again.";

export const STATUS_TITLE: Record<LeadStatus, string> = {
  our_move: 'Our move',
  follow_up: 'Follow up',
  their_move: 'Their move',
  older: 'Older than two months',
};
const STATUS_HINT: Record<LeadStatus, string> = {
  our_move: 'They wrote or called and we have not answered, or a task is past due.',
  follow_up: 'We reached out last, by text or email - worth a nudge if it goes quiet.',
  their_move: 'Joseph or Robert called after their last word (for an agent, any reply of ours).',
  older: 'Nothing in the last 60 days.',
};
export const REASON_WORDS: Record<LeadReason, string> = {
  unanswered: 'Unanswered',
  no_touch: 'Not contacted yet',
  broken_promise: 'Task overdue',
  gone_quiet: 'Gone quiet',
};
const REASON_TONE: Record<LeadReason, string> = {
  unanswered: 'bg-red-50 text-red-700 border-red-200',
  no_touch: 'bg-amber-50 text-amber-800 border-amber-200',
  broken_promise: 'bg-red-50 text-red-700 border-red-200',
  gone_quiet: 'bg-gray-50 text-gray-600 border-gray-200',
};
const ROLES: LeadRole[] = ['tenant', 'buyer', 'seller'];
const ROLE_WORDS: Record<LeadRole, string> = { tenant: 'Tenant', buyer: 'Buyer', seller: 'Seller' };
const CHANNEL: Record<LeadTouch['channel'], string> = { text: 'Text', call: 'Call', email: 'Email', other: 'Message' };

const DAY = 86_400_000;
function when(iso: string | null | undefined, now: number): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const date = new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' });
  const days = Math.floor((now - t) / DAY);
  return days <= 0 ? `${date} (today)` : days === 1 ? `${date} (yesterday)` : `${date} (${days}d ago)`;
}

/** "+13233526671" -> "(323) 352-6671"; anything else as GoHighLevel has it. Text only - the call goes through CallInGhl. */
export function phoneWords(phone: string): string {
  const d = phone.replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : phone;
}

const byWord = (by: LeadTouch['by']) => (by === 'joseph' ? 'Joseph' : by === 'robert' ? 'Robert' : by === 'other' ? 'Team' : null);

function Touch({ label, touch, now }: { label: string; touch: LeadTouch | null; now: number }) {
  if (!touch) return <div className="text-xs text-gray-400">{label}: none logged</div>;
  const who = byWord(touch.by);
  return (
    <div className="min-w-0 text-xs text-gray-600">
      <span className="font-medium text-gray-700">{label}:</span> {CHANNEL[touch.channel]}{who && label === 'Out' ? ` by ${who}` : ''}, {when(touch.at, now)}
      {touch.preview && <span className="block truncate text-gray-500">"{touch.preview}"</span>}
    </div>
  );
}

export function LeadCard({ lead, now }: { lead: LeadRow; now: number }) {
  return (
    <li className="min-w-0 rounded-lg border border-gray-200 bg-white p-4" data-testid="lead-row">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-words text-sm font-semibold text-gray-900">{lead.name}</span>
            {lead.is_agent && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">Agent</span>}
            {lead.roles.map((r) => (
              <span key={r} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{ROLE_WORDS[r]}</span>
            ))}
          </div>
          {lead.properties.length > 0 && (
            <div className="mt-0.5 text-xs text-gray-500">{lead.properties.map((p) => p.label).join(' · ')}</div>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {lead.phone && <span className="text-xs tabular-nums text-gray-500">{phoneWords(lead.phone)}</span>}
          <CallInGhl url={lead.ghl_contact_url} />
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {lead.reasons.map((r) => (
          <span key={r} className={`rounded-full border px-2 py-0.5 text-xs font-medium ${REASON_TONE[r]}`}>{REASON_WORDS[r]}</span>
        ))}
        <span className="text-xs text-gray-600">{lead.activity === 'reading' ? 'Reading their messages...' : lead.why}</span>
      </div>

      {lead.activity === 'unreadable' && (
        <p role="status" className="mt-2 text-xs text-red-700">Couldn't read this lead's messages from GoHighLevel - the page will try again.</p>
      )}

      {lead.activity === 'read' && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <Touch label="In" touch={lead.last_inbound} now={now} />
          <Touch label="Out" touch={lead.last_outbound} now={now} />
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        {lead.days_since_our_touch !== null && (
          <span>Our last touch: {lead.days_since_our_touch === 0 ? 'today' : `${lead.days_since_our_touch}d ago`}</span>
        )}
        <span className={lead.overdue_tasks ? 'font-medium text-red-700' : undefined}>
          Tasks: {lead.open_tasks} open{lead.overdue_tasks ? `, ${lead.overdue_tasks} overdue` : ''}
        </span>
        {lead.next_task && <span className="min-w-0 truncate">Next: {lead.next_task.title}{lead.next_task.due_at ? ` (${when(lead.next_task.due_at, now)})` : ''}</span>}
      </div>

      {lead.note && (
        <p className="mt-2 line-clamp-3 whitespace-pre-line break-words rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700">
          <span className="font-medium text-gray-900">{lead.note.pinned ? 'Pinned note' : 'Latest note'}:</span> {lead.note.body}
        </p>
      )}
    </li>
  );
}

export default function Leads() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || ALL;
  const [roles, setRoles] = useState<LeadRole[]>([]);
  const [board, setBoard] = useState<LeadsBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const b = await api.leads.get();
      setBoard(b);
      setError(null);
    } catch (e) {
      setError(e instanceof Error && e.message ? `${NOT_READ} (${e.message})` : NOT_READ);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  // While the server is still reading leads, ask again; stop as soon as every row is read.
  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    if (board && (board.refreshing || board.reading > 0)) timer.current = window.setTimeout(() => void load(), POLL_MS);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [board, load]);

  const now = board ? Date.parse(board.read_at) : Date.now();
  const tabs = board?.tabs ?? [];
  const activeTab = tab === ALL || tabs.some((t) => t.tag === tab) ? tab : ALL;

  const shown = useMemo(() => (board?.rows ?? []).filter((r) =>
    (activeTab === ALL || r.properties.some((p) => p.tag === activeTab))
    && (roles.length === 0 || roles.some((role) => r.roles.includes(role)))), [board, activeTab, roles]);

  const groups = useMemo(() => {
    const order: LeadStatus[] = ['our_move', 'follow_up', 'their_move', 'older'];
    return order.map((s) => ({ status: s, rows: shown.filter((r) => r.status === s) })).filter((g) => g.rows.length > 0);
  }, [shown]);

  const pickTab = (t: string) => {
    const next = new URLSearchParams(params);
    if (t === ALL) next.delete('tab'); else next.set('tab', t);
    setParams(next, { replace: true });
  };
  const toggleRole = (r: LeadRole) => setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  const PILL = 'shrink-0 rounded-full border px-3 py-1 text-sm font-medium transition-colors';
  const ON = 'border-gray-900 bg-gray-900 text-white';
  const OFF = 'border-gray-200 bg-white text-gray-700 hover:border-gray-400';

  return (
    <Layout>
      <div className="mx-auto max-w-4xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">
              Leads
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Everyone tagged <span className="font-medium">lead</span> in GoHighLevel - whose move it is first.
            </p>
          </div>
          <Button variant="outline" onClick={() => void load()} isLoading={loading}>Refresh</Button>
        </div>

        {error && (
          <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span className="min-w-0 break-words">{error}</span>
            <Button size="sm" variant="outline" onClick={() => void load()} isLoading={loading}>Try again</Button>
          </div>
        )}
        {!board && !error && <p className="py-16 text-center text-gray-500">Loading leads from GoHighLevel...</p>}

        {board && (
          <>
            <nav aria-label="Properties" className="-mx-4 mb-3 flex gap-2 overflow-x-auto px-4 pb-1">
              <button type="button" role="tab" aria-selected={activeTab === ALL} onClick={() => pickTab(ALL)} className={`${PILL} ${activeTab === ALL ? ON : OFF}`}>
                All leads ({board.rows.length})
              </button>
              {tabs.map((t) => (
                <button key={t.tag} type="button" role="tab" aria-selected={activeTab === t.tag} onClick={() => pickTab(t.tag)}
                  className={`${PILL} ${activeTab === t.tag ? ON : OFF}`}>
                  {t.label} ({t.count})
                </button>
              ))}
            </nav>

            <div className="mb-4 flex flex-wrap items-center gap-2" aria-label="Filter by role">
              {ROLES.map((r) => (
                <button key={r} type="button" aria-pressed={roles.includes(r)} onClick={() => toggleRole(r)}
                  className={`rounded-full border px-3 py-0.5 text-xs font-medium ${roles.includes(r) ? ON : OFF}`}>
                  {ROLE_WORDS[r]}
                </button>
              ))}
              {(board.refreshing || board.reading > 0) && (
                <span role="status" className="text-xs text-gray-500">
                  Reading messages from GoHighLevel{board.reading > 0 ? ` - ${board.reading} to go` : ''}...
                </span>
              )}
            </div>

            {shown.length === 0 && <p className="py-10 text-center text-sm text-gray-500">No leads here.</p>}

            {groups.map((g) => (
              <section key={g.status} aria-label={STATUS_TITLE[g.status]} className="mb-8">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{STATUS_TITLE[g.status]} ({g.rows.length})</h2>
                <p className="mb-3 text-xs text-gray-400">{STATUS_HINT[g.status]}</p>
                <ul className="space-y-3">
                  {g.rows.map((r) => <LeadCard key={r.contact_id} lead={r} now={now} />)}
                </ul>
              </section>
            ))}
          </>
        )}
      </div>
    </Layout>
  );
}
